import Head from 'next/head';
import { useRouter } from 'next/router';
import { useState } from 'react';
import useScrollspy from '../hooks/scrollSpy';
import { useInView } from 'react-intersection-observer';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import {
  Box,
  Container,
  Flex,
  Grid,
  GridItem,
  Heading,
  Text,
  Link,
  List,
  ListItem,
  UnorderedList,
  keyframes,
} from '@terminal/design-system';

import LayoutSimple from '../components/LayoutSimple';
import metaRobots from '../helpers/metaRobots';
import sharecardImage from '../public/images/cost-of-remote/cost-of-remote-sharecard.jpg';
import heroBgImage from '../public/images/cost-of-remote/hero.svg';
import footerBgImage from '../public/images/cost-of-remote/advantage-bg.svg';
import ImageShimmer from '../components/ImageShimmer/ImageShimmer';
import sitePath from '../helpers/sitePath';
import BuildReportContactHandler from '../components/Forms/BuildReportContactHandler';

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  };
}

const list = [
  {
    label: 'Market exploration',
    href: 'market-exploration',
  },
  {
    label: 'Business setup',
    href: 'business-setup',
  },
  {
    label: 'Recruiting',
    href: 'recruiting',
  },
  {
    label: 'Employee support and retention',
    href: 'employee-support',
  },
  {
    label: 'Ongoing administrative costs',
    href: 'administrative-costs',
  },
  {
    label: 'the Terminal Advantage',
    href: 'terminal-advantage',
  },
];

const NavListHero = () => {
  return (
    <List spacing="1rem">
      {list.map((item) => (
        <ListItem key={item.label} color="#475f7a" opacity="0.7">
          <Link
            href={`#${item.href}`}
            _hover={{ textDecoration: 'none' }}
            transition="all 0.3s ease"
          >
            {item.label}
          </Link>
        </ListItem>
      ))}
    </List>
  );
};

const NavList = ({ time, cost, annualCost }) => {
  const ids = list.map((item) => item.href);
  const activeId = useScrollspy(ids, 3);

  const TotalBox = ({ title, total, type, bgColor }) => {
    const formatTotal = (value) => {
      if (type === 'currency') {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(value);
      } else {
        const years = Math.floor(value / 12);
        const dur2 = value / 12 - years;
        const months = Math.floor(dur2 * 12);
        const yearLabel = years > 1 ? years + ' years' : years + ' year';
        const monthLabel = months !== 0 ? ', ' + months + ' months' : '';
        return yearLabel + monthLabel;
      }
    };

    return (
      <Box py="1rem" pl="1rem" bgColor={bgColor}>
        <Box textTransform="uppercase" fontWeight="semibold" opacity="0.6" color="white">
          {title}
        </Box>
        <Box color="white" fontWeight="bold" fontSize="1.325rem">
          {formatTotal(total)}
        </Box>
      </Box>
    );
  };

  return (
    <Flex flexDirection="column" justifyContent="space-between" height="100%">
      <List spacing="1rem" mb="2rem" pl="1.5rem">
        {list.map((item) => (
          <ListItem key={item.label} color="#475f7a">
            <Link
              href={`#${item.href}`}
              _hover={{ textDecoration: 'none' }}
              fontWeight={item.href === activeId ? 'semibold' : 'normal'}
              opacity={item.href === activeId ? '1' : '0.7'}
              position="relative"
              _before={{
                content: '""',
                position: 'absolute',
                height: '0.125rem',
                width: '1rem',
                left: '-1.5rem',
                top: '0.5rem',
                bgColor: item.href === activeId ? '#475f7a' : 'transparent',
              }}
              transition="all 0.3s ease"
            >
              {item.label}
            </Link>
          </ListItem>
        ))}
      </List>
      <Box mt="4rem">
        <TotalBox title="Total Time" total={time} type="time" bgColor="#5c7590" />
        <TotalBox title="Initial Cost" total={cost} type="currency" bgColor="#475f7a" />
        <TotalBox title="Annual Cost" total={annualCost} type="currency" bgColor="#3a516a" />
      </Box>
    </Flex>
  );
};

const HeroTitle = ({ title }) => (
  <Heading
    as="h4"
    fontSize="1rem"
    mb="2rem"
    color="white"
    textTransform="uppercase"
    position="relative"
    _before={{
      content: '""',
      width: '4.375rem',
      height: '0.125rem',
      bgColor: 'white',
      position: 'absolute',
      top: '0',
      bottom: '0',
      margin: 'auto',
      left: '-5.25rem',
    }}
  >
    {title}
  </Heading>
);

const HeroHeading = ({ title }) => (
  <Heading as="h3" mb="2rem" color="#f7ed69" fontSize="1.625rem">
    {title}
  </Heading>
);

const IconWithText = ({ title, image, text }) => (
  <>
    <GridItem>
      <ImageShimmer
        src={image}
        alt="icon"
        width="70"
        height="70"
        style={{ objectFit: 'contain' }}
        layout="intrinsic"
      />
    </GridItem>
    <GridItem>
      <Text fontSize="1.125rem" mb="0.5rem" color="white">
        {title}
      </Text>
      <Text color="white">{text}</Text>
    </GridItem>
  </>
);

const elementsOfBuilding = [
  {
    image: '/images/cost-of-remote/HR-icon.svg',
    title: 'Human Resources',
    text: 'HR partners are the go-to resources for employee-related issues. HR partners typically manage tasks such as job design, employee relations, performance management, and the nuances of employment law in different regions.',
  },
  {
    image: '/images/cost-of-remote/benefits-icon.svg',
    title: 'Payroll + benefits',
    text: 'These partners handle payroll processing and oversee the benefits packages you offer your employees specific to the regions you operate in.',
  },
  {
    image: '/images/cost-of-remote/recruiting-icon.svg',
    title: 'Recruiting',
    text: 'Recruiters will help you find the best candidates in the market where you’re building your team. They’re responsible for managing the search for potential employees, screening them, and matching them to available job positions. They’ll also likely have established candidate relationships in the region you’ve chosen, helping to fast-track your search.',
  },
  {
    image: '/images/cost-of-remote/real-estate-icon.svg',
    title: 'Real estate + IT infrastructure',
    text: 'Corporate real estate partners will help you find the right place to establish an office and manage the property once you’re there. Keep in mind that even remote teams prefer an office to go into at least 1-2 days a week, an important consideration once stay-at-home guidelines are lifted.',
  },
  {
    image: '/images/cost-of-remote/operations-icon.svg',
    title: 'Business operations',
    text: 'Each market will have a different business regime in which you will need to operate within to be compliant and ensure you respect the local laws and requirements. This varies by country and will even vary by state, province, or city. You will need third-party support consultants and partners for banking, insurance, tax, and accounting.',
  },
];

const threeTypesOfCosts = [
  {
    image: '/images/cost-of-remote/hard-costs-icon.svg',
    title: 'Hard costs',
    text: 'These are the costs you’ve probably already considered, like travel time, salaries and equipment.',
  },
  {
    image: '/images/cost-of-remote/hidden-costs-icon.svg',
    title: 'Hidden costs',
    text: 'As the name would suggest, these are all the costs that may not be obvious, like building your brand in a new market and retaining employees.',
  },
  {
    image: '/images/cost-of-remote/time-to-team-icon.svg',
    title: 'Time to team',
    text: 'Developing your own remote strategy means that it might take years before your team is hired, onboarded, and ready to work.',
  },
];

const terminalAdvantage = [
  {
    image: '/images/cost-of-remote/recruiting-icon.svg',
    title: 'High-quality recruiting, fast',
    text: 'Our in-market expertise and existing database of tech talent translates to drastically reduced hiring timelines. Businesses that work with Terminal have teams fully hired and ramped up in less than six months; businesses that choose DIY solutions are typically just beginning the interview process in that same timeframe.',
  },
  {
    image: '/images/cost-of-remote/market-icon.svg',
    title: 'Optimized market selection',
    text: 'We’ve built powerful, data-backed algorithms to help you find the perfect market for your remote team. We assess thousands of data points in categories like average salary, cost of living, and size of talent pool to help you navigate the tradeoffs and identify the right geographic markets — saving you time, effort, and money in the process.',
  },
  {
    image: '/images/cost-of-remote/benefits-icon.svg',
    title: 'Seamless payroll + benefits',
    text: "Not only do Terminal's member benefits packages help you close great candidates, but they also keep employees happier, so they stay with you for the long run. Our experts also provide support with immigration, payroll, and other clerical tasks so you can focus on impactful projects and avoid wasting time on red tape.",
  },
  {
    image: '/images/cost-of-remote/engagement-icon.svg',
    title: 'Employee-tested engagement + retention programs',
    text: 'Our peer-curated learning resources, career planning, and mentoring programs are employee-tested and have a track-record of increasing retention for the long haul. That can save you thousands in turnover costs each year.',
  },
];

const SectionDetails = ({ title, children }) => (
  <Box>
    <Heading as="h2" color="#475f7a" mb="-1rem" pl="2.5rem">
      {title}
    </Heading>
    <Box p="3rem" bgColor="#eef4f6">
      {children}
    </Box>
  </Box>
);

const SectionLabel = ({ title }) => (
  <Box>
    <Heading
      display="inline-block"
      as="h4"
      color="white"
      my="2.5rem"
      p="0.5rem 1rem"
      fontSize="1rem"
      textTransform="uppercase"
      bgColor="#475f7a"
    >
      {title}
    </Heading>
  </Box>
);

const SmallTitle = ({ title }) => (
  <Heading as="h4" fontSize="1.125rem" mb="1rem" fontWeight="medium">
    {title}
  </Heading>
);

const Price = ({ animation, children }) => (
  <Box
    mt="3.5rem"
    mb="1.5rem"
    p="1.5rem 0.5rem"
    borderTop="0.0625rem solid #e7e7e7"
    borderBottom="0.0625rem solid #e7e7e7"
    fontWeight="medium"
    animation={animation}
  >
    {children}
  </Box>
);

const highlight = keyframes`
  20% {
    background-color: #87B75C;
    color: white;
    border-top-color: #87B75C;
    border-bottom-color: #87B75C;
  }
  100% {
    background-color: transparent;
    color: inherit;
  }
`;

const highlightAnimation = `${highlight} 2s ease`;

const CalcTrigger = ({ children, price, setCost, setAnnualCost, setTime }) => {
  const { ref, inView } = useInView({
    rootMargin: '-50% 0%',
    triggerOnce: true,
  });

  if (inView) {
    setCost && setCost();
    setAnnualCost && setAnnualCost();
    setTime && setTime();
  }

  return (
    <Box ref={ref}>
      {price && <Price animation={inView && highlightAnimation}>{children}</Price>}
      {!price && <Box>{children}</Box>}
    </Box>
  );
};

const SectionTotal = ({ children }) => (
  <Box p="1.5rem 3rem" mt="2rem" bgColor="rgba(135,183,92,.12)" borderTop="0.0625rem solid #87b75c">
    <Heading as="h3" fontSize="1.625rem" color="#87b75c">
      {children}
    </Heading>
  </Box>
);

const Testimonial = ({ text, name, job }) => (
  <Box pl="8">
    <Box
      fontSize={{ base: '1.6rem', lg: '2rem' }}
      lineHeight={{ base: '1.9rem', lg: '2.375rem' }}
      mb={{ base: '1.2rem', lg: '1.5rem' }}
      position="relative"
    >
      <Box
        fontSize={{ base: '2.8rem', lg: '3.5rem' }}
        position="absolute"
        left="-2rem"
        fontWeight="700"
        color="#F7ED69"
      >
        “
      </Box>
      <Box color="#92b7c6" fontWeight="bold">
        {text}
      </Box>
    </Box>
    <Box fontSize="1rem" lineHeight="1.125rem" fontWeight="700" position="relative">
      <Box color="#475f7a">{name}</Box>
      <Box color="#475f7a" fontStyle="italic">
        {job}
      </Box>
    </Box>
  </Box>
);

const footer = {
  longText: false,
  text: 'Find the scale you need. Build your team today.',
};

export default function CostOfRemote() {
  const router = useRouter();

  const meta = {
    title:
      'The Cost of Remote: 34 real (and hidden) costs to consider when building a remote team - Terminal.io',
    description:
      'A DIY approach to building remote teams is costly. Explore Terminal’s interactive breakdown of hard and hidden costs and explore your options for building a global team of engineers.',
  };

  const [time, setTime] = useState(0);
  const [cost, setCost] = useState(0);
  const [annualCost, setAnnualCost] = useState(0);

  return (
    <>
      <Head>
        <link rel="canonical" href={sitePath(router.asPath)} />
        <title>{meta.title}</title>
        <meta property="og:title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={sitePath(router.asPath)} />
        <meta property="og:image" content={sharecardImage.src} />
        <meta name="robots" content={metaRobots({ translated: false, locale: router.locale })} />
      </Head>
      <LayoutSimple type="businesses" footer={footer} withCTA>
        <Box
          bgImage={{
            base: 'linear-gradient(180deg, #92B7C6 0%, #475F7A 100%)',
            lg: `url(${heroBgImage.src}), linear-gradient(180deg, #92B7C6 0%, #475F7A 100%)`,
          }}
          bgPosition="top right"
          bgRepeat="no-repeat"
          p={{ base: '6rem 0' }}
        >
          <Container>
            <Link href="/" display="block" width="8.75rem">
              <svg
                width="220px"
                height="63px"
                viewBox="0 0 101 30"
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                  <g transform="translate(0.132626, 0.132626)" fill="#FFFFFF" fillRule="nonzero">
                    <path d="M39.801061,12.9920424 L36.1286472,12.9920424 L36.1286472,10.862069 L45.8925729,10.862069 L45.8925729,12.9920424 L42.2201592,12.9920424 L42.2201592,22.7877984 L39.801061,22.7877984 L39.801061,12.9920424 Z M52.5928382,21.0331565 C51.8050398,22.1233422 50.6405836,23.0437666 48.6697613,23.0437666 C46.0543767,23.0437666 44.1724138,21.204244 44.1724138,18.2732095 C44.1724138,15.3421751 46.0888594,13.5026525 48.5623342,13.5026525 C51.0344828,13.5026525 52.9164456,15.3421751 52.9164456,18.2732095 L52.9164456,18.9376658 L46.5384615,18.9376658 C46.6100796,20.3342175 47.362069,21.0848806 48.6339523,21.0848806 C49.8527851,21.0848806 50.4973475,20.301061 50.9628647,19.6710875 L52.5928382,21.0331565 L52.5928382,21.0331565 Z M50.6578249,17.2851459 C50.5503979,16.1432361 49.8156499,15.4456233 48.5623342,15.4456233 C47.3793103,15.4456233 46.627321,16.2798408 46.5384615,17.2851459 L50.6578249,17.2851459 Z M54.061008,13.7586207 L56.265252,13.7586207 L56.265252,14.7122016 L56.301061,14.7122016 C56.6591512,14.0477454 57.4124668,13.5875332 58.2533156,13.5875332 C58.5397878,13.5875332 58.8262599,13.6392573 59.1127321,13.7572944 L58.7546419,15.9045093 C58.5039788,15.8196286 58.2533156,15.7679045 58.0198939,15.7679045 C57.0172414,15.7679045 56.3899204,16.3806366 56.3899204,17.8289125 L56.3899204,22.7864721 L54.061008,22.7864721 L54.061008,13.7586207 Z M59.8169761,13.7586207 L61.9482759,13.7586207 L61.9482759,14.627321 L61.9840849,14.627321 C62.3965517,14.0132626 63.2559682,13.5026525 64.2771883,13.5026525 C65.2984085,13.5026525 66.2480106,14.0132626 66.6962865,14.6949602 L66.7320955,14.6949602 C67.1976127,14.030504 68.1114058,13.5026525 69.204244,13.5026525 C71.5159151,13.5026525 72.8952255,14.7811671 72.8952255,16.6034483 L72.8952255,22.7877984 L70.7639257,22.7877984 L70.7639257,17.0636605 C70.7639257,16.2453581 70.3156499,15.5477454 69.1153846,15.5477454 C68.0225464,15.5477454 67.4854111,16.1618037 67.4854111,16.9442971 L67.4854111,22.7877984 L65.3541114,22.7877984 L65.3541114,17.0636605 C65.3541114,16.2453581 64.8169761,15.5477454 63.7055703,15.5477454 C62.5941645,15.5477454 62.0755968,16.1618037 62.0755968,16.9442971 L62.0755968,22.7877984 L59.8183024,22.7877984 L59.8183024,13.7586207 L59.8169761,13.7586207 L59.8169761,13.7586207 Z M74.5397878,12.4469496 L76.8687003,12.4469496 L76.8687003,10.3514589 L74.5397878,10.3514589 L74.5397878,12.4469496 Z M74.5397878,22.7877984 L76.8687003,22.7877984 L76.8687003,13.7586207 L74.5397878,13.7586207 L74.5397878,22.7877984 Z M78.5225464,13.7586207 L80.7625995,13.7586207 L80.7625995,14.627321 L80.7984085,14.627321 C81.2824934,14.030504 82.2493369,13.5026525 83.306366,13.5026525 C85.4018568,13.5026525 86.6750663,14.7466844 86.6750663,16.8766578 L86.6750663,22.7891247 L84.3461538,22.7891247 L84.3461538,17.4217507 C84.3461538,16.2466844 83.9522546,15.5477454 82.6803714,15.5477454 C81.5344828,15.5477454 80.8527851,16.229443 80.8527851,17.2679045 L80.8527851,22.7877984 L78.5238727,22.7877984 L78.5238727,13.7586207 L78.5225464,13.7586207 L78.5225464,13.7586207 Z M93.7838196,21.9190981 L93.7480106,21.9190981 C93.7480106,21.9190981 93.0848806,23.0437666 91.168435,23.0437666 C88.7135279,23.0437666 87.7466844,21.7320955 87.7466844,20.3687003 C87.7466844,18.7506631 88.6962865,18.0862069 90.9721485,17.5570292 C93.229443,17.0291777 93.4270557,16.8408488 93.4270557,16.3302387 C93.4270557,15.8196286 92.9429708,15.3766578 91.8143236,15.3766578 C90.3992042,15.3766578 90.0411141,16.1604775 89.8076923,16.8594164 L87.872679,16.0928382 C88.2851459,14.7122016 89.5026525,13.5026525 91.7785146,13.5026525 C93.9098143,13.5026525 95.7374005,14.4734748 95.7374005,17.0636605 L95.7374005,22.7877984 L93.7851459,22.7877984 L93.7851459,21.9190981 L93.7838196,21.9190981 L93.7838196,21.9190981 Z M89.9854111,20.2161804 C89.9854111,20.897878 90.5583554,21.1366048 91.3832891,21.1366048 C92.9058355,21.1366048 93.4973475,20.4721485 93.4973475,19.8076923 L93.4973475,18.2400531 L93.4615385,18.2400531 C93.3541114,18.4270557 93.2824934,18.6485411 91.4363395,19.0915119 C90.1100796,19.397878 89.9854111,19.8408488 89.9854111,20.2161804 L89.9854111,20.2161804 Z M97.3156499,22.7877984 L99.734748,22.7877984 L99.734748,10.3514589 L97.3156499,10.3514589 L97.3156499,22.7877984 Z" />
                    <path d="M28.2625995,0.0596816976 L17.4496021,0.0596816976 L17.4496021,8.78381963 L0,8.78381963 L0,19.596817 L8.72413793,19.596817 L8.72413793,28.3209549 L19.5371353,28.3209549 L19.5371353,10.872679 L28.2612732,10.872679 L28.2612732,0.0596816976 L28.2625995,0.0596816976 Z M17.4496021,26.2334218 L10.8129973,26.2334218 L10.8129973,17.5092838 L2.08885942,17.5092838 L2.08885942,10.872679 L17.4496021,10.872679 L17.4496021,26.2334218 Z M26.1737401,8.78381963 L19.5384615,8.78381963 L19.5384615,2.14854111 L26.1737401,2.14854111 L26.1737401,8.78381963 Z" />
                  </g>
                </g>
              </svg>
            </Link>
            <Grid gap={{ base: '2rem' }} gridTemplateColumns={{ md: '6fr 4fr' }}>
              <GridItem>
                <Box pl={{ lg: '20' }}>
                  <Heading
                    as="h1"
                    my="1rem"
                    color="white"
                    fontSize={{ base: '2.5rem', lg: '3rem' }}
                  >
                    The Cost of Remote:
                  </Heading>
                  <Heading as="h2" color="white" fontSize={{ base: '2rem', lg: '2.25rem' }}>
                    34 real (and hidden) costs to consider when building a remote team
                  </Heading>
                </Box>
              </GridItem>
              <GridItem>
                <Box
                  p="2.5rem 3rem 2rem"
                  bgColor="white"
                  boxShadow="0rem 0.25rem 0.5rem rgba(0, 0, 0, 0.16)"
                  maxW="21.25rem"
                  mb="6rem"
                >
                  <Box
                    color="#475f7a"
                    opacity="0.6"
                    fontWeight="bold"
                    textTransform="uppercase"
                    mb="2rem"
                  >
                    Contents:
                  </Box>
                  <NavListHero />
                </Box>
              </GridItem>
            </Grid>
            <Box pl={{ lg: '20' }} maxW="48rem">
              <HeroTitle title="Introduction" />
              <Text color="white" mb="4">
                There are countless benefits to building a remote team, such as increasing your
                talent pool, bringing on diverse perspectives, and reducing competition. But often,
                the catalyst for remote growth is reducing cost.
              </Text>
              <Text color="white" mb="4">
                At Terminal, we help companies of all types build out a global presence. We’ve seen
                firsthand how unexpected costs and delays can crop up at nearly every turn. The
                knowledge we’ve accumulated to build, operate, and scale remote teams hasn’t come
                easy, and businesses draw on our experience to navigate the process and minimize
                costs along the way.
              </Text>
              <Text color="white" mb="14">
                So, what exactly goes into hiring and retaining talent in global markets? Are you
                prepared for the time, expense, and mental resources that come with doing it
                yourself? Based on our experience launching teams across North and South America,
                this guide breaks down the hard and hidden costs to help you identify the best
                pathway for your business.
              </Text>
              <HeroHeading title="The elements of building a remote team" />
              <Text color="white" mb="4">
                Whether it’s setting up a remote workspace, legally creating an overseas entity,
                managing payroll and benefits, or simply recruiting talent, it takes a lot of extra
                time, effort, and money to set up a global presence from a distance.
              </Text>
              <Text color="white" mb="14">
                Many businesses opt to block and tackle the bigger tasks, outsourcing them to a firm
                that specializes in those areas. Here are the most common areas your business may
                seek help:
              </Text>
              <Grid gridTemplateColumns="4.5rem 1fr" gap="2.5rem 1.75rem" mb="2.5rem">
                {elementsOfBuilding.map((item) => (
                  <IconWithText
                    key={item.title}
                    image={item.image}
                    title={item.title}
                    text={item.text}
                  />
                ))}
              </Grid>
              <Text color="white" mb="5rem">
                There’s no single way of embarking on your remote journey, but there are ways that
                may be more costly and time-intensive. This guide aims at breaking down all of the
                elements of remote to help you decide how to approach it at your company.
              </Text>
              <HeroTitle title="Legend" />
              <HeroHeading title="The three types of costs we’ll cover in this guide" />
            </Box>
            <Box pl={{ lg: '20' }} maxW="992px">
              <Grid gridTemplateColumns={{ lg: '1fr 1fr 1fr' }} gap="2.5rem" mb="2.5rem">
                {threeTypesOfCosts.map((item) => (
                  <Grid
                    display={{ base: 'grid', lg: 'block' }}
                    key={item.title}
                    gridTemplateColumns={{ base: '4.5rem 1fr', lg: 'none' }}
                    gap={{ base: '2.5rem 1.75rem', lg: '0' }}
                  >
                    <GridItem mb={{ lg: '1.5rem' }}>
                      <ImageShimmer
                        src={item.image}
                        alt="icon"
                        width="70"
                        height="70"
                        style={{ objectFit: 'contain' }}
                        layout="intrinsic"
                      />
                    </GridItem>
                    <GridItem>
                      <Text fontSize="1.125rem" mb="0.5rem" color="white">
                        {item.title}
                      </Text>
                      <Text color="white">{item.text}</Text>
                    </GridItem>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Container>
        </Box>
        <Box bgColor="#87b75c" py="3rem">
          <Container>
            <Box pl={{ lg: '20' }} maxW="992px">
              <Heading as="h3" color="white" fontSize="1.625rem" mb="2rem">
                Want more details? Download this step-by-step list to going DIY remote – from hiring
                to setting up a legal entity, to finding real estate and more.
              </Heading>
              <Box px={3} maxW="440px" mx="auto">
                <BuildReportContactHandler
                  buttonTitle="GET GUIDE"
                  thankYouUrl={'/guides/thank-you?slug=cost-of-remote'}
                  formType="Cost of Remote"
                  theme={{ green200: '#324F60' }}
                  completed={false}
                  textColor="white"
                  showLabels={true}
                />
              </Box>
            </Box>
          </Container>
        </Box>
        <Container>
          <Grid gridTemplateColumns={{ lg: '3fr 10fr', xl: '2.5fr 10fr' }} gap={{ lg: '12' }}>
            <GridItem display={{ base: 'none', lg: 'block' }}>
              <Box p="4rem 0" position="sticky" top="0" height="100vh">
                <NavList time={time} cost={cost} annualCost={annualCost} />
              </Box>
            </GridItem>
            <GridItem overflow="hidden">
              <Box id="market-exploration" pt="6rem">
                <CalcTrigger setTime={() => setTime(12)}>&nbsp;</CalcTrigger>
                <SectionDetails title="Market exploration">
                  <Text>
                    Choosing the wrong market is the most expensive mistake you can make while
                    building a global presence. Typically, by the time you realize the market may
                    not be a good fit for your company or talent needs, contracts will have been
                    signed and hundreds of thousands of dollars will have been spent. That’s why the
                    exploration phase is so crucial.
                  </Text>
                </SectionDetails>
                <Box px={{ sm: '12' }}>
                  <SectionLabel title="Hard Costs" />
                  <SmallTitle title="Staff Salary" />
                  <Text mb="1rem">
                    If you are just beginning to hire globally, it’s imperative that the appropriate
                    team resources are set aside for this endeavor. You’ll need to dedicate two team
                    members to research and explore the market, and make it their primary focus for
                    at least a quarter. Otherwise, it won’t be properly prioritized, and it could
                    take months before research starts in earnest.
                  </Text>
                  <Text mb="1rem">
                    Keep in mind that market exploration will go far beyond Google searches. You’ll
                    need time to tap into knowledge sources on the ground in your target area to get
                    the job done. Exploring a new market requires proactive team members that are
                    ready to travel and forge relationships. That costs time and money. Ideally, at
                    least one team member will have a firm grasp on the native language of each
                    country represented on your list of target markets. A three-month commitment is
                    25% of their annual salary, and that’s before they even start to implement
                    takeaways from their research and findings.
                  </Text>
                  <CalcTrigger price setCost={() => setCost(70000)}>
                    Cost: $70,000 (2 employees full time for 90 days @$140K per year)
                  </CalcTrigger>
                  <SmallTitle title="Travel costs" />
                  <Text mb="1rem">
                    When travel allows, it’s ideal to actually spend some time in the area where
                    you’re thinking of establishing a presence, both to explore local professional
                    groups and meet with possible HR or recruiting partners. Once stay-at-home
                    guidelines are lifted, it’s going to occupy a lot of your team’s time. Be
                    prepared for the amount of travel needed to feel confident in your decision to
                    commit to a market. At Terminal, we have collected enough data to know that you
                    should narrow your search down to three markets and spend a week minimum in each
                    one in order to get the essential legwork done. Those costs will start to add
                    up.
                  </Text>
                  <CalcTrigger price setCost={() => setCost(90000)}>
                    Cost: $20,000
                  </CalcTrigger>
                  <SectionLabel title="Hidden Costs" />
                  <SmallTitle title="Opportunity costs" />
                  <Text mb="2rem">
                    Whoever you dedicate to scope out a market won’t be able to focus on other
                    tasks, and depending on their experience, the learning curve can be quite steep.
                    Sure, this person knows your business, but have they hired remotely before? And
                    if so, was it based on existing relationships? If you put a more experienced
                    person on the task, it might cost your company in other ways as well. If you
                    take a cofounder or c-level executive out of their day-to-day role, what will
                    that cost your company? Even if they aren’t doing the bulk of the gruntwork,
                    your executives are probably going to have to travel to the markets you’re
                    considering, which could eat up a significant portion of their annual salary and
                    working time.
                  </Text>
                  <SmallTitle title="In-market relationship building" />
                  <Text mb="1rem">
                    Getting set up in a new global market means getting to know the firms and
                    professionals you’ll need to be working with. It can involve trips, phone calls,
                    and general relationship building with real estate brokers, accounting firms,
                    and legal teams. Without an experienced remote teams partner or market expert,
                    this process can take up a lot of valuable time and money. Don’t neglect to
                    factor this into your market exploration costs.
                  </Text>
                  <SectionLabel title="Time to team" />
                  <Text mb="1rem">
                    Your international remote team is just a sparkle in your eye at this point. It’s
                    going to be a long road until you have remote employees set up. Keep in mind
                    that your company is likely an unknown entity in the new market, so it may take
                    extra time to build a brand and attract top talent in market.
                  </Text>
                  <Text mb="4rem">
                    The market exploration phase takes, on average, about one year.
                  </Text>
                  <Box mb="4rem">
                    <ImageShimmer
                      src="/images/cost-of-remote/market-exploration.svg"
                      alt="Market exploration"
                      width="770"
                      height="82"
                      style={{ objectFit: 'contain' }}
                      layout="intrinsic"
                    />
                  </Box>
                </Box>
                <SectionTotal>Estimated total market exploration cost: $90,000</SectionTotal>
              </Box>
              <Box id="business-setup" pt="6rem">
                <CalcTrigger setTime={() => setTime(18)}>&nbsp;</CalcTrigger>
                <SectionDetails title="Business setup">
                  <Text pb="5rem">
                    After you’ve chosen your market, you’ll have to get ready to legally employ
                    people in the region. But keep in mind: The effort and financial commitment
                    needed in this phase are substantial. The steps are neither straightforward nor
                    uniform for every market. The most costly part of this phase is hiring outside
                    consultants to navigate legal and regulatory issues, and it’s a cost that can’t
                    be avoided. Your in-house team will almost certainly lack the specialization
                    necessary for a new market. You’ll have to bring in outside help, and once you
                    do, costs go up.
                  </Text>
                </SectionDetails>
                <Box bgColor="#475f7a" p="3rem" mx="2.5rem" mt="-5rem" maxW={{ lg: '38rem' }}>
                  <Text fontSize="1.125rem" color="white" fontWeight="bold" mb="1rem">
                    Legal questions to consider in your new market
                  </Text>
                  <UnorderedList color="white" spacing="2">
                    <ListItem>
                      What are the local laws you need to know to hire and operate?
                    </ListItem>
                    <ListItem>What incentive programs might you be eligible for?</ListItem>
                    <ListItem>What are the nuances of employment law?</ListItem>
                    <ListItem>
                      What are your legal liabilities if you need to let someone go?
                    </ListItem>
                    <ListItem>
                      How can you mitigate exposure in how you hire, fire, structure, and employ
                      your team?
                    </ListItem>
                  </UnorderedList>
                </Box>
                <Box px={{ sm: '12' }} mt="10">
                  <SectionLabel title="Hard Costs" />
                  <CalcTrigger setAnnualCost={() => setAnnualCost(40000)}>
                    <SmallTitle title="Setting up an entity" />
                  </CalcTrigger>
                  <Text mb="1rem">
                    If you plan to hire a foreign team, you need to set up a new entity. Each
                    country is unique and requires local legal expertise. You’ll not only need to
                    pay to register your entity, you’ll also incur legal fees and accountant fees to
                    get set up. Keep in mind you’ll also need to associate your entity with a
                    registered local address. If you plan to have a physical location, this often
                    prompts companies to start paying rent before they have the headcount to
                    populate a new space.
                  </Text>
                  <CalcTrigger price setCost={() => setCost(140000)}>
                    Cost: $50,000{' '}
                    <Box as="span" color="rgb(71, 95, 122)" fontWeight="semibold">
                      Ongoing entity costs:
                    </Box>{' '}
                    <Box as="em">
                      The price of setting up an entity might sound reasonable, but you must think
                      about what you will need to start paying once it’s up and running. It costs an
                      average of $40,000 a year just to keep an entity.
                    </Box>
                  </CalcTrigger>
                  <SectionLabel title="Hard Costs" />
                  <SmallTitle title="Bank accounts" />
                  <Text mb="1rem">
                    You won’t be able to run your new entity through your domestic bank accounts.
                    You will need a bank account in your local market to process everything from
                    your payroll to tax credits. For setup, it’s most important to consider fees for
                    account set up and deposits, as well as initial capital requirements. Keep in
                    mind that both of these can vary by market.
                  </Text>
                  <CalcTrigger price setCost={() => setCost(142000)}>
                    Cost: $2,000
                  </CalcTrigger>
                  <SmallTitle title="Office buildout" />
                  <Text mb="1rem">
                    According to a recent study, remote employees say they prefer going into an
                    office at least 1-2 days a week. Even with stay-at-home guidelines in effect,
                    you may want to start the search for an office or co-working space so you’re
                    ready when things return to normal. That might involve construction and
                    remodeling to get your office space up to snuff. It also means furniture,
                    computers, and even kitchen supplies. And don’t forget that your new office will
                    require just as much care as HQ. White walls and empty halls lead to faster
                    turnover, so consider branded decor and maybe even a few mood-lifting plants.
                    Even if you plan on using a service like WeWork, you’ll still have to add things
                    like software subscriptions, telephone and internet services, and event space to
                    your calculations.
                  </Text>
                  <CalcTrigger price setCost={() => setCost(297000)}>
                    $50,000 - $155,000
                  </CalcTrigger>
                  <SmallTitle title="Staff salary" />
                  <Text mb="1rem">
                    As with market exploration, you’re going to need employees on your staff
                    navigating the process of setting up your global remote presence. In this phase,
                    you’ll be looping in more executive-level staff and getting feedback and buy-in
                    from people across your organization. These staff members need to follow the
                    legal and financial intricacies of launching in the decided market, and they’ll
                    likely need to involve your legal and finance teams.
                  </Text>
                  <CalcTrigger price setCost={() => setCost(385000)}>
                    Cost: $88,000
                  </CalcTrigger>
                  <SmallTitle title="Broker fees" />
                  <Text mb="1rem">
                    Finding real estate for your new office will be a big undertaking, and it’ll be
                    hard to do it without a broker who knows the market. Real estate brokers can be
                    expensive, but they are extremely helpful for a few reasons – they know your
                    market and they understand local requirements to get into a space. They’ll take
                    a commission of your rent after you’re set up, but they can help you negotiate
                    rent, set a rent cap, and secure financial incentives on your behalf.
                  </Text>
                  <CalcTrigger price setCost={() => setCost(390000)}>
                    Cost: $5,000
                  </CalcTrigger>
                  <SectionLabel title="Hidden Costs" />
                  <SmallTitle title="Opportunity cost" />
                  <Text mb="1rem">
                    It bears repeating: Any time you have internal staff members working on your
                    global remote presence, they’ll be taking time away from their usual job
                    functions. Getting your company set up to hire a team in a new country will
                    require overcoming a lot of legal and administrative hurdles. These tasks can
                    easily overtake your employees’ working hours.
                  </Text>
                  <SmallTitle title="Committing to the wrong market" />
                  <Text mb="1rem">
                    What if you start spending money setting yourself up in a new market, only to
                    hit problems you can’t overcome? What if, for example, you learn the talent pool
                    was favorable only for the first skill sets you needed but will cap your growth
                    in the next stage? Or what if local competition is particularly fierce for the
                    specific type of engineer you need to hire, or a certain non-standard benefit is
                    required? If you’ve already sunk money into a new market, you won’t be able to
                    recoup those costs.
                  </Text>
                  <SectionLabel title="Time to team" />
                  <Text mb="4rem">
                    It can take a long time to set up everything you need to start hiring in a new
                    country. Each and every internal decision can take a long time, depending on how
                    many stakeholders are involved and what issues you come across along the way.
                    Budget in 4 to 6 months for this phase, understanding that you’re still not
                    going to have any engineers working on your remote team yet.
                  </Text>
                  <Box mb="4rem">
                    <ImageShimmer
                      src="/images/cost-of-remote/business-setup.svg"
                      alt="Business setup"
                      width="770"
                      height="82"
                      style={{ objectFit: 'contain' }}
                      layout="intrinsic"
                    />
                  </Box>
                </Box>
                <SectionTotal>Estimated total setup cost: $195,000 - $300,000</SectionTotal>
              </Box>
              <Box id="recruiting" pt="6rem">
                <SectionDetails title="Recruiting">
                  <Text>
                    The beauty of having a global presence is the ability to tap into talent pools
                    you otherwise wouldn’t have access to. But hiring in a new market takes much
                    more than just a “build it and they will come” mentality. We’ve found that your
                    hiring strategy is strongest when when recruiters are deeply familiar with the
                    engineering landscape in each market, available skillsets, and the particular
                    demands of remote work. This kind of familiarity can come at a premium.
                  </Text>
                </SectionDetails>
                <Box px={{ sm: '12' }}>
                  <CalcTrigger setTime={() => setTime(24)}>&nbsp;</CalcTrigger>
                  <SectionLabel title="Hard Costs" />
                  <SmallTitle title="Recruiters" />
                  <Text mb="1rem">
                    Whether you opt to retain an in-market recruiting agency or hire a full-time
                    staff member, you will need to allocate funds to acquire the recruiting power
                    necessary to staff your new team. In North America, in urban tech hubs, you can
                    expect to spend upwards of $125,000 for one full-time recruiter, and you’ll
                    likely need to hire more than one if you’re scaling your international team. If
                    you plan to go the agency route, you can expect to pay between 15 and 25 percent
                    of every new hire’s salary. That means hiring 15 engineers with a $90,000 salary
                    could come with an additional $300,000 fee.
                  </Text>
                  <CalcTrigger price setCost={() => setCost(690000)}>
                    Estimated cost: $300,000 and up
                  </CalcTrigger>
                  <SmallTitle title="Staff salary" />
                  <Text mb="1rem">
                    No one wants to spend time interviewing a candidate who’s clearly a bad fit.
                    It’s frustrating, and it’s also expensive: each hour your team spends in an
                    interview is an hour they’re not working on impactful projects. Your business is
                    still paying your team for these hours, which can equate to thousands of
                    dollars, depending on salaries. You can expect your team to spend 3 hours
                    minimum per first-round interview. Experienced recruiters will screen away
                    candidates who aren’t a good fit, saving your staff time and your business
                    money. This makes selecting the right recruiter particularly important.
                  </Text>
                  <CalcTrigger price setCost={() => setCost(700000)}>
                    Estimated cost: $10,000
                  </CalcTrigger>
                  <SmallTitle title="In-person interviews (when applicable)" />
                  <Text mb="1rem">
                    The pandemic has made Zoom calls the norm for pretty much every type of work
                    transaction, interviews included. But once stay-at-home guidelines are lifted,
                    many teams may still prefer face-to-face meetings for final-round interviews,
                    especially for critical roles. These interviews entail airfare, lodging, and
                    transportation, which can add up fast.
                  </Text>
                  <CalcTrigger price setCost={() => setCost(707500)}>
                    Estimated cost: $7,500 and up for a 15-person team
                  </CalcTrigger>
                  <SmallTitle title="Brand recognition in the new market" />
                  <Text mb="1rem">
                    It’s entirely possible that no one will know about your company in the new
                    market, so how will you attract talent? Why will engineers want to work with you
                    instead of a local company? How will you stand out amongst the competition? It
                    will take some valuable time and money to show engineers why you’re worthy of
                    their commitment. You might consider employing a local PR team, placing
                    advertisements, and when allowed again, sponsoring local events, hosting
                    influencer dinners, and throwing introductory happy hours.
                  </Text>
                  <CalcTrigger price setCost={() => setCost(807500)}>
                    Cost: $100,000
                  </CalcTrigger>
                  <SectionLabel title="Hidden Costs" />
                  <SmallTitle title="Low acceptance rates" />
                  <Text mb="1rem">
                    Building an effective recruiting strategy comes down to building an efficient
                    candidate funnel. When you hire recruiters who know the market, you not only get
                    higher-quality leads, but you also get interviews that are more seamless and
                    higher acceptance rates when you extend an offer. The opposite is also true:
                    recruiters unfamiliar with local interviewing culture may get great leads, but a
                    faulty interview process can turn off otherwise excellent candidates. Each time
                    a candidate rejects an offer, that’s time, energy, and money that your company
                    won’t get back.
                  </Text>
                  <SmallTitle title="Cost of vacancies" />
                  <Text mb="1rem">
                    Every new job opening is created for the same reason: to help your business
                    achieve something it couldn’t otherwise with your current team. The quicker you
                    fill a role, the faster you’re achieving your expanded company objectives. When
                    you let roles sit vacant, you’re accomplishing less, and chances are you’re also
                    distributing additional responsibilities to your current team members, which
                    left unchecked, can lead to morale issues and turnover.
                  </Text>
                  <SmallTitle title="Cost of bad hires" />
                  <Text mb="1rem">
                    According to the U.S. Department of Labor, the average cost of each bad hire is
                    about 30 percent of that individual&apos;s annual earnings. Some experts argue
                    it’s much higher. However you cut it, hiring the wrong person is a costly risk
                    of recruiting. Not only do you spend time and money onboarding the wrong person,
                    you also have to go back to the drawing board if you eventually have to part
                    ways. Keep in mind that legal liabilities around terminations can be very strict
                    in some regions; severance or off-boarding benefits can be a huge hidden cost.
                  </Text>
                  <SectionLabel title="Time to team" />
                  <Text mb="1rem">
                    It takes an average of 35 days to hire a new software engineer. This means for a
                    team of five, you can expect to spend around 4 to 6 months to build your remote
                    team. Keep in mind that when you work with recruiters who are unfamiliar with
                    the market, or who set up a poor candidate pipeline, this timeframe can go up
                    dramatically. That’s why it’s important to build a strong candidate funnel to
                    optimize every step of the process.
                  </Text>
                  <Box mb="4rem">
                    <ImageShimmer
                      src="/images/cost-of-remote/recruiting.svg"
                      alt="Recruiting"
                      width="770"
                      height="82"
                      style={{ objectFit: 'contain' }}
                      layout="intrinsic"
                    />
                  </Box>
                </Box>
                <SectionTotal>
                  Estimated total recruiting cost: $417,500, increasing as you scale
                </SectionTotal>
                <Box px={{ sm: '12' }}>
                  <Box bgColor="#475f7a" p={{ base: '2rem 1.5rem', lg: '2.5rem 3.5rem' }} my="4rem">
                    <Grid gridTemplateColumns={{ md: '1fr 1fr 1fr' }} gap="2.5rem" mb="2.5rem">
                      <GridItem>
                        <Box
                          fontSize={{ base: '2rem', lg: '2.25rem' }}
                          fontWeight="bold"
                          color="white"
                          mb="1rem"
                        >
                          1 in 5
                        </Box>
                        <Text color="white">
                          leaders report not having a strategy for finding talent in a remote work
                          environment.
                        </Text>
                      </GridItem>
                      <GridItem>
                        <Box
                          fontSize={{ base: '2rem', lg: '2.25rem' }}
                          fontWeight="bold"
                          color="white"
                          mb="1rem"
                        >
                          40%
                        </Box>
                        <Text color="white">
                          leaders report not having a strategy for finding talent in a remote work
                          environment.
                        </Text>
                      </GridItem>
                      <GridItem>
                        <Box
                          fontSize={{ base: '2rem', lg: '2.25rem' }}
                          fontWeight="bold"
                          color="white"
                          mb="1rem"
                        >
                          90%
                        </Box>
                        <Text color="white">
                          of engineers say the interview process is fundamentally broken.
                        </Text>
                      </GridItem>
                    </Grid>
                    <Text color="white">
                      <Box as="em">
                        Source: Terminal’s{' '}
                        <Link href="/engineering-2020" textDecoration="underline">
                          Engineer 2020 Report
                        </Link>
                      </Box>
                    </Text>
                  </Box>
                  <Testimonial
                    text="Recruiting is where the rubber hits the road when setting up a global remote team. Cutting corners will only lead to heartache in the future."
                    name="Kerri McKinney"
                    job="Global Director, Talent Acquisition, Terminal"
                  />
                </Box>
              </Box>
              <Box id="employee-support" pt="6rem">
                <SectionDetails title="Employee support and retention">
                  <Text>
                    Once you’ve chosen your market and set up operations there, that’s when the real
                    work -- and expenditures -- begin. Keeping a remote team running smoothly is a
                    huge lift, considering the number of moving parts: onboarding, coaching and
                    guidance, and immigration support to name a few. Going DIY on these tasks isn’t
                    just tedious; it also can hit your ledgers hard.
                  </Text>
                </SectionDetails>
                <Box px={{ sm: '12' }}>
                  <SectionLabel title="Hard Costs" />
                  <CalcTrigger setCost={() => setCost(942500)}>
                    <SmallTitle title="Benefits" />
                  </CalcTrigger>
                  <Text mb="1rem">
                    The benefits packages you offer in new markets may look similar to your
                    stateside packages, but you will likely be working with different providers for
                    a new market. Expect to find in-market specialists to square away your employee
                    insurance plans, pensions, taxable health benefit adjustments, and employee
                    travel coordination and support.
                  </Text>
                  <CalcTrigger price setAnnualCost={() => setAnnualCost(175000)}>
                    Estimated cost: $135,000 annually
                  </CalcTrigger>
                  <CalcTrigger setTime={() => setTime(34)}>&nbsp;</CalcTrigger>
                  <SmallTitle title="Onboarding" />
                  <Text>
                    It’s hard to overstate the importance of a strong onboarding program. According
                    to recent studies, companies that focus on onboarding retain new employees 50%
                    longer than companies that don’t. And employees agree: 69% of workers who have a
                    positive onboarding experience say they plan on staying with their new employers
                    for 3 years or longer. A great HR partner will bring remote-specific onboarding
                    programs that can drastically improve retention.
                  </Text>
                  <CalcTrigger setAnnualCost={() => setAnnualCost(190000)}>&nbsp;</CalcTrigger>
                  <CalcTrigger setCost={() => setCost(975500)} price>
                    Estimated cost: $15,000 / year, ($3,000 per employee x 5 new employees per year)
                  </CalcTrigger>
                  <SmallTitle title="Learning and development" />
                  <Text mb="1rem">
                    58% of engineers cite learning and development as one of their top criteria when
                    considering a new role. No matter how brilliant your engineers are, mentorship
                    and guidance can make all the difference. Your engineers are much more likely to
                    feel supported in their careers when you offer meaningful coaching to keep them
                    feeling creative. But remember, building out a coaching program requires a lot
                    of forethought to feel authentic. It’s important to get it right; otherwise you
                    may come across as patronizing to your experienced engineers.
                  </Text>
                  <CalcTrigger setCost={() => setCost(997500)} price>
                    Estimated cost: $40,000 / year and up ($8,000 per employee x 5 new employees in
                    the first year)
                  </CalcTrigger>
                  <CalcTrigger setAnnualCost={() => setAnnualCost(240000)}>&nbsp;</CalcTrigger>
                  <SmallTitle title="Payroll" />
                  <Text mb="1rem">
                    If you want to keep your employees around, you’ll have to pay them of course!
                    The nitty gritty of getting salaries in your employees’ pockets is often an
                    after-thought, but issuing checks, maintaining tax compliance, and calculating
                    payroll deductions and garnishments is a lot of work. Keep in mind that
                    international regulations around payroll almost always differ from what you do
                    stateside, so having an in-market expert is a must.
                  </Text>
                  <CalcTrigger setCost={() => setCost(1003500)} price>
                    Estimated cost: $6,000 / year and up ($100 per employee per month x 5 new
                    employees in the first year)
                  </CalcTrigger>
                  <CalcTrigger setAnnualCost={() => setAnnualCost(246000)}>&nbsp;</CalcTrigger>
                  <CalcTrigger setCost={() => setCost(1004700)}>
                    <SmallTitle title="eNPS surveys" />
                  </CalcTrigger>
                  <Text mb="1rem">
                    HR, done right, is like getting a temperature check on the health of your
                    workforce. The gold standard is conducting a regular employee net promoter score
                    survey, or eNPS survey, to understand how happy your employees are. Surveys are
                    notoriously difficult to get right (there are whole PhD programs devoted to
                    survey design!), and the survey itself may be the easy part. Getting employees
                    to fill out the survey can take up huge amounts of time in its own right. You’ll
                    want to do this semi-annually, which can turn into a huge lift if done in-house.
                  </Text>
                  <CalcTrigger price setAnnualCost={() => setAnnualCost(247200)}>
                    Estimated cost: $1,200 / year
                  </CalcTrigger>
                  <SmallTitle title="Culture-building programs" />
                  <Text mb="1rem">
                    Now that you have new remote engineers, you’ll want to keep them. This means
                    investing in new programs to keep your employees happy, like company culture
                    initiatives and team-building events. After all, strong company culture requires
                    a strong bond between teams. It’s an investment that’s well worth your time:
                    According to a study at Stanford University, teams that collaborate work 48%
                    longer and are more likely to solve problems correctly.
                  </Text>
                  <CalcTrigger price setAnnualCost={() => setAnnualCost(257200)}>
                    Estimated cost: $10,000 / year and up ($2,000 per employee x 5 employees in the
                    first year)
                  </CalcTrigger>
                  <SmallTitle title="Staff salary" />
                  <Text mb="1rem">
                    Implementation is quite possibly the most tedious phase of setting up a global
                    remote team. Setting up human resources services and a region-specific
                    onboarding flow is easier with local experts, but they still take up a
                    significant amount of time for someone on your team to implement and maintain.
                    Don’t underestimate how much time it will take for your team to keep your
                    infrastructure up and running.
                  </Text>
                  <CalcTrigger price setAnnualCost={() => setAnnualCost(277200)}>
                    Estimated cost: $20,000 / year
                  </CalcTrigger>
                  <SectionLabel title="Hidden Costs" />
                  <SmallTitle title="Immigration support" />
                  <Text mb="2rem">
                    The bureaucracy around immigration knows no bounds. Navigating work permits
                    yourself can turn into a full-time job; asking your employees to trudge through
                    red tape, you might as well be sending them directly to job boards to find a new
                    job. Depending on where you set up shop, expect to invest hundreds of tedious
                    hours filling out paperwork with various government agencies.
                  </Text>
                  <SmallTitle title="Offboarding" />
                  <Text mb="2rem">
                    Of course, employees don’t stay forever. Whether they’re resigning or leaving
                    involuntarily, you’ll need to have a consistent offboarding plan. There are
                    always more loose ends than you expect, from transferring job responsibilities
                    to deactivating access rights and turning in equipment. And it’s best not to
                    forget exit interviews: they can offer invaluable insights into how to increase
                    retention. Conducting this from afar can lead to things falling through the
                    cracks.
                  </Text>
                  <SmallTitle title="Disengaged employees" />
                  <Text mb="2rem">
                    While you might be tempted to find a local devshop to shortcut the recruiting
                    process, keep in mind that these are often structured on a project-to-project
                    basis, and accordingly, developers may not be particularly engaged with your
                    company or its mission. People who are bought into your business’s vision will
                    be more excited to solve the problems your company faces, and they’ll also help
                    inspire those around them, a virtuous cycle that’s hard to quantify.
                  </Text>
                  <SmallTitle title="Opportunity costs" />
                  <Text mb="4rem">
                    Maintaining your global remote infrastructure will have to become part of the
                    ongoing goals for some of your team members, and it will have to come at the
                    expense of other things. Be careful that whoever ends up with it on their plate
                    doesn’t feel like they’re getting stuck with an unsavory responsibility.
                    Otherwise, it might breed resentment and cause your staff to look for greener
                    pastures.
                  </Text>
                  <Box mb="2rem">
                    <Testimonial
                      text="I can’t stress this enough: it’s almost impossible to over-invest in employee retention. Many companies focus solely on getting the perfect candidate to join their team, but keeping each new hire happy for the long term is really the key to a well-functioning, productive team."
                      name="Kerri McKinney"
                      job="Global Director, Talent Acquisition, Terminal"
                    />
                  </Box>
                  <SectionLabel title="Time to team" />
                  <Text mb="1rem">
                    Your team might be hired and eager to work, but it still might take some time
                    before they can meaningfully contribute to your team. Consider that they will
                    need to be onboarded, introduced to your team, and set up with equipment -- all
                    things that will take more time if your team has to set up every new process
                    first.
                  </Text>
                  <Text mb="1rem">
                    Plus, if you don’t have good employee retention, you’ll constantly be setting up
                    new employees and diminishing the amount of time where you have fully functional
                    team members.
                  </Text>
                  <Text mb="1rem">
                    Expect to spend 6 to 8 months getting your processes set up and about 30 to 60
                    days onboarding each new employee.
                  </Text>
                  <Box my="4rem">
                    <ImageShimmer
                      src="/images/cost-of-remote/employee-support.svg"
                      alt="Recruiting"
                      width="646"
                      height="99"
                      style={{ objectFit: 'contain' }}
                      layout="intrinsic"
                    />
                  </Box>
                  <SectionTotal>Estimated costs (re-occuring annually): $227,200</SectionTotal>
                </Box>
              </Box>
              <Box id="administrative-costs" pt="6rem">
                <SectionDetails title="Ongoing administrative costs">
                  <Text>
                    Building an airplane from scratch is one thing; keeping it flying is another
                    task completely. Once you’ve established your remote team, expect to spend a lot
                    of time and energy on routine maintenance. Here are a few of the biggest line
                    items you’ll have to consider.
                  </Text>
                </SectionDetails>
                <Box px={{ sm: '12' }} mt="4rem">
                  <CalcTrigger setCost={() => setCost(1094700)}>
                    <SmallTitle title="Bookkeeping and accountant fees" />
                  </CalcTrigger>
                  <Text mb="1rem">
                    Finding a trustworthy, savvy accountant is a tall order under the best of
                    circumstances. But when you throw complex local tax laws into the mix, expect
                    hefty price tags for in-market specialists. And that’s the best case scenario.
                    If you choose a substandard accountant, it may cost your company even more, as
                    poorly managed books can lead to thousands in unnecessary taxes and/or
                    penalties. And remember, as adept as your team is back at HQ, attempting to
                    manage all receipts and local spending from your main office will almost always
                    be more expensive and less efficient.
                  </Text>
                  <CalcTrigger price setAnnualCost={() => setAnnualCost(337200)}>
                    Cost: $60,000 annually
                  </CalcTrigger>
                  <CalcTrigger setCost={() => setCost(1099700)}>
                    <SmallTitle title="Entity compliance" />
                  </CalcTrigger>
                  <Text mb="1rem">
                    Every region has its own set of laws for conducting business there, so you’ll
                    need your local legal and finance teams to dedicate time to get it right.
                    Recurring requirements vary by country, but most regions require the following
                    at a minimum: insurance policies (general commercial liabilities, director &amp;
                    officer liabilities), year-end tax filings and remittances (including income
                    tax, sales tax, and payroll tax), and labor law compliance. Getting any one of
                    these wrong can lead to big penalties or even the loss of your business license
                    in extreme cases, so the stakes are high.
                  </Text>
                  <CalcTrigger price setAnnualCost={() => setAnnualCost(342200)}>
                    Cost: $5,000 annually
                  </CalcTrigger>
                  <CalcTrigger setCost={() => setCost(1159700)}>
                    <SmallTitle title="Operations" />
                  </CalcTrigger>
                  <Text mb="1rem">
                    Behind every great office is a great office coordinator. They take care of the
                    day-to-day logistics around the office, and hiring the right one will save you
                    time and money in the long run. Keep in mind your search will likely require
                    more than just hunting for current office coordinators on Linkedin. The best
                    office coordinators tend to come from unexpected places – we’ve seen excellent
                    office managers hired from the restaurant industry, the military, and even from
                    an opera house (really!) – so expect to spend time with a local agency to
                    recruit, screen, and sign the perfect fit.
                  </Text>
                  <CalcTrigger price setAnnualCost={() => setAnnualCost(402200)}>
                    Cost: $60,000 annually
                  </CalcTrigger>
                  <CalcTrigger setCost={() => setCost(1239700)}>
                    <SmallTitle title="IT support" />
                  </CalcTrigger>
                  <Text mb="1rem">
                    When you have employees working remotely or from a second office, your team’s
                    productivity hinges on your communication infrastructure. A lot can go wrong to
                    interrupt that -- faulty routers, issues with Zoom, password lockouts -- so
                    expect to set aside funds for IT support. And keep in mind that your remote
                    teams will likely have remote-specific IT issues that your experts at HQ may not
                    be as familiar with.
                  </Text>
                  <CalcTrigger price setAnnualCost={() => setAnnualCost(482200)}>
                    Cost: ~ $80,000 annually
                  </CalcTrigger>
                  <CalcTrigger setCost={() => setCost(1339700)}>
                    <SmallTitle title="Rent" />
                  </CalcTrigger>
                  <Text mb="1rem">
                    And let’s not forget rent! You will most likely work with your real estate
                    broker to find a space that you and your team can grow into, and that means you
                    are on the hook for paying a high monthly rate for the size of your team. If you
                    expect your team to continue to grow, you’ll have to periodically find new
                    office space as well. Along with that will come additional brokerage fees.
                  </Text>
                  <CalcTrigger price setAnnualCost={() => setAnnualCost(582200)}>
                    Estimated cost: $100,000 annually
                  </CalcTrigger>
                </Box>
                <SectionTotal>Total administrative costs: $295,000 annually</SectionTotal>
                <Box
                  my="4rem"
                  p={{ base: '2rem', lg: '3.5rem 6rem' }}
                  bgImage="linear-gradient(180deg,#92b7c6,#475f7a)"
                >
                  <Heading as="h2" color="white" fontSize={{ base: '2rem', lg: '2.25rem' }}>
                    Total cost and time for building a remote global team on your own:
                  </Heading>
                  <Flex
                    mt="1rem"
                    py="1.5rem"
                    flexDirection={{ base: 'column', md: 'row' }}
                    alignItems={{ md: 'center' }}
                    borderBottom="0.0625rem solid #fff"
                  >
                    <Box
                      textTransform="uppercase"
                      color="#f7ed69"
                      fontWeight="semibold"
                      w={{ md: '30%' }}
                    >
                      Total Time
                    </Box>
                    <Box color="white" fontWeight="bold" fontSize="1.625rem">
                      2 years, 10 months
                    </Box>
                  </Flex>
                  <Flex
                    mt="1rem"
                    py="1.5rem"
                    flexDirection={{ base: 'column', md: 'row' }}
                    alignItems={{ md: 'center' }}
                    borderBottom="0.0625rem solid #fff"
                  >
                    <Box
                      textTransform="uppercase"
                      color="#f7ed69"
                      fontWeight="semibold"
                      w={{ md: '30%' }}
                    >
                      Initial Cost
                    </Box>
                    <Box color="white" fontWeight="bold" fontSize="1.625rem">
                      $1,339,700
                    </Box>
                  </Flex>
                  <Flex
                    mt="1rem"
                    py="1.5rem"
                    flexDirection={{ base: 'column', md: 'row' }}
                    alignItems={{ md: 'center' }}
                  >
                    <Box
                      textTransform="uppercase"
                      color="#f7ed69"
                      fontWeight="semibold"
                      w={{ md: '30%' }}
                    >
                      Annual Cost
                    </Box>
                    <Box color="white" fontWeight="bold" fontSize="1.625rem">
                      $582,200
                    </Box>
                  </Flex>
                </Box>
              </Box>
            </GridItem>
          </Grid>
        </Container>
        <Box
          bgImage={{
            base: 'linear-gradient(180deg,#475f7a 36.87%,#2d445d)',
            lg: `url(${footerBgImage.src}), linear-gradient(180deg,#475f7a 36.87%,#2d445d)`,
          }}
          bgPosition={{ base: 'center', lg: 'calc(100% - 120px) top, center' }}
          bgRepeat="no-repeat"
          p={{ base: '6rem 0' }}
          id="terminal-advantage"
        >
          <Container>
            <Box pl={{ lg: '20' }} maxW="55rem">
              <Heading as="h2" my="1rem" color="white" fontSize={{ base: '2.5rem', lg: '3rem' }}>
                Save time and cut costs with the Terminal Advantage
              </Heading>
              <Text color="white" mb="4">
                A global team of top talent can help fuel your product growth and bottom line, but
                many companies vastly underestimate the costs associated with building this team
                themselves.
              </Text>
              <Text color="white" mb="4">
                Establishing a global remote team is a daunting task. The stakes are high, and so is
                the price tag. Between finding the right market, setting up operations, and then
                maintaining the team once established, you can expect to spend hundreds of thousands
                of dollars.
              </Text>
              <Text color="white" mb="4">
                But, luckily, there’s another option. Terminal supports companies through every step
                of hiring and retaining a team, and by minimizing the risks of setting up in a new
                market and reducing the costs associated with remote hiring, we’ll help you win the
                talent wars.
              </Text>
              <Text color="white" mb="8">
                We help businesses build a global presence using real-world tested strategies that
                get remote teams up and running in a fraction of the time and cost of alternatives.
              </Text>
              <Grid gridTemplateColumns="4.5rem 1fr" gap="2.5rem 1.75rem" mb="2.5rem">
                {terminalAdvantage.map((item) => (
                  <IconWithText
                    key={item.title}
                    image={item.image}
                    title={item.title}
                    text={item.text}
                  />
                ))}
              </Grid>
              <Text color="white" mb="5rem">
                There’s no one-size-fits-all when it comes to building your remote team. Connect
                with us to share your goals and we can build a roadmap for global hiring that takes
                your needs into account. Our remote team experts are standing by to help you grow.
              </Text>
            </Box>
            <Box pl={{ lg: '20' }}>
              <Grid
                gridTemplateColumns={{ lg: '2fr 8fr' }}
                gridGap={{ base: '8', lg: '24', xl: '40' }}
              >
                <GridItem>
                  <Heading as="h2" color="white" fontSize={{ base: '2.5rem', lg: '3rem' }}>
                    About Terminal
                  </Heading>
                </GridItem>
                <GridItem>
                  <Text color="white">
                    Terminal is a remote teams engine for fast-growing companies. This means we
                    hire, develop, and retain engineering teams — so that you can focus on building
                    brilliant products. Today, we build remote teams across North America, Mexico
                    and Latin America, supported by our unique model that blends recruiting, HR,
                    ops, and community to drive growth.
                  </Text>
                </GridItem>
              </Grid>
            </Box>
          </Container>
        </Box>
      </LayoutSimple>
    </>
  );
}
